import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { LoginBrand } from '../components/auth/LoginBrand';
import { useAuth } from '../contexts/AuthContext';
import { resolveAuthEmail } from '../lib/authEmail';
import { IS_UNIFIED_PORTAL, portalHomePath, readPortalChoice } from '../lib/portal';

function ShieldIcon() {
  return (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2 4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3Zm-1 14-3.5-3.5 1.41-1.42L11 13.17l4.59-4.58L17 10l-6 6Z"
        fill="currentColor"
      />
    </svg>
  );
}

export function NotAuthorizedPage() {
  const { session, authUser, deniedEmail, accessStatus, signOut, loading } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email =
    deniedEmail ?? resolveAuthEmail(authUser) ?? authUser?.email ?? null;

  if (!loading && !session) {
    return <Navigate to="/login" replace />;
  }

  if (!loading && accessStatus === 'allowed') {
    if (IS_UNIFIED_PORTAL) {
      const choice = readPortalChoice();
      return <Navigate to={choice ? portalHomePath(choice) : '/choose-portal'} replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (!loading && accessStatus === 'choose_portal') {
    return <Navigate to="/choose-portal" replace />;
  }

  async function handleSignOut() {
    setBusy(true);
    setError(null);
    try {
      await signOut();
      navigate('/login', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign out failed');
      setBusy(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card not-authorized-card">
        <LoginBrand />

        <div className="not-authorized-card__body">
          {loading ? (
            <p className="not-authorized-card__loading">Checking access…</p>
          ) : (
            <>
              <div className="not-authorized-card__icon" aria-hidden>
                <ShieldIcon />
              </div>

              <p className="not-authorized-card__eyebrow">Allowlist required</p>
              <h2 className="not-authorized-card__title">Access not enabled</h2>

              {email ? (
                <>
                  <p className="not-authorized-card__lead">
                    This Microsoft account is not on the CRM allowlist yet.
                  </p>
                  <div className="not-authorized-card__email-pill">
                    <span className="not-authorized-card__email-label">Signed in as</span>
                    <span className="not-authorized-card__email">{email}</span>
                  </div>
                </>
              ) : (
                <p className="not-authorized-card__lead">
                  We could not read your Microsoft work email from this sign-in. Your account is not
                  on the CRM allowlist yet.
                </p>
              )}

              <div className="not-authorized-card__callout">
                <p className="not-authorized-card__callout-title">What to do next</p>
                <ol className="not-authorized-card__steps">
                  <li>
                    Ask an admin or IT to add your work email under{' '}
                    <strong>Settings → Allowed emails</strong>
                  </li>
                  <li>The allowlist email must match your Microsoft login exactly</li>
                  <li>Sign out below, then sign in again after you are added</li>
                </ol>
              </div>

              {error && <div className="alert alert-error">{error}</div>}

              <button
                type="button"
                className="btn-secondary not-authorized-card__signout"
                disabled={busy}
                onClick={() => void handleSignOut()}
              >
                {busy ? 'Signing out…' : 'Sign out'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
